'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["value", "restricted", "width", "left"],
  _excluded2 = ["className", "disabled", "min", "minLabel", "max", "maxLabel", "trackStart", "trackEnd", "onChange", "onRawValueChange", "readOnly", "size", "showLabels", "showTickMarks", "step", "tabIndex", "value", "restrictedAreaTooltip"];
import devLogger from 'react-utils/devLogger';
import PropTypes from 'prop-types';
import { Component } from 'react';
import classNames from 'classnames';
import Small from '../elements/Small';
import H5 from '../elements/headings/H5';
import UITooltip from '../tooltip/UITooltip';
import Controllable from '../decorators/Controllable';
import { callIfPossible } from '../core/Functions';
import SyntheticEvent from '../core/SyntheticEvent';
import UISliderTickMark from './internal/UISliderTickMark';
import { UISliderTrackSegment } from './internal/UISliderTrack';
import UIAbstractSlider from './abstract/UIAbstractSlider';
import { getTabIndex } from '../utils/TabIndex';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getFirstDefinedValue = (...values) => values.find(val => val != null);
const getAdjustedValue = (min, max, step, rawValue) => {
  if (rawValue < min) return min;
  if (rawValue > max) return max;
  if (Math.abs(rawValue % step) !== 0) {
    return Math.round(rawValue / step) * step;
  }
  return rawValue;
};
const getControlPositionFromValue = (min, max, adjustedValue) => {
  if (max - min === 0) {
    return 0;
  }
  return (adjustedValue - min) / (max - min) * 100;
};
const SLIDER_LABELS = {
  max: 'private-slider__label--max',
  min: 'private-slider__label--min'
};
class UISlider extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      isDragging: false
    };
    this.handleChange = rawValue => {
      const {
        disabled,
        max,
        min,
        onChange,
        onRawValueChange,
        readOnly,
        step,
        value,
        trackStart,
        trackEnd
      } = this.props;
      if (disabled || readOnly) {
        return;
      }
      const adjustedRawValue = getAdjustedValue(trackStart, trackEnd, step, rawValue);
      callIfPossible(onRawValueChange, SyntheticEvent(adjustedRawValue));
      const newValue = getAdjustedValue(min, max, step, rawValue);
      if (value !== newValue) {
        onChange(SyntheticEvent(newValue));
      }
    };
    this.handleKeyDown = evt => {
      const {
        step,
        value
      } = this.props;
      let nextValue = value || 0;
      if (evt.key === 'ArrowLeft' || evt.key === 'ArrowDown') {
        evt.preventDefault();
        nextValue = value - step;
      } else if (evt.key === 'ArrowRight' || evt.key === 'ArrowUp') {
        evt.preventDefault();
        nextValue = value + step;
      }
      this.handleChange(nextValue);
      callIfPossible(this.props.onKeyDown, evt);
    };
    this.handleDrag = evt => {
      const {
        percentX
      } = evt.target.value;
      this.handleChange(this.getControlValueFromPercentX(percentX));
    };
    this.handleDragStart = () => {
      const {
        onDragStart
      } = this.props;
      this.setState({
        isDragging: true
      });
      callIfPossible(onDragStart);
    };
    this.handleDragEnd = () => {
      const {
        onDragEnd
      } = this.props;
      this.setState({
        isDragging: false
      });
      callIfPossible(onDragEnd);
    };
    this.handleMinLabelClick = () => {
      const {
        disabled,
        min,
        readOnly
      } = this.props;
      if (!disabled && !readOnly) this.handleChange(min);
    };
    this.handleMaxLabelClick = () => {
      const {
        disabled,
        max,
        readOnly
      } = this.props;
      if (!disabled && !readOnly) this.handleChange(max);
    };
  }
  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isDragging && this.state.isDragging) {
      this.addBodyDragCursorClass();
    } else if (prevState.isDragging && !this.state.isDragging) {
      this.removeBodyDragCursorClass();
    }
  }
  componentWillUnmount() {
    this.removeBodyDragCursorClass();
  }
  addBodyDragCursorClass() {
    document.body.classList.add('hubspot-drag-cursor');
  }
  removeBodyDragCursorClass() {
    document.body.classList.remove('hubspot-drag-cursor');
  }
  getControlValueFromPercentX(percentX) {
    const min = getFirstDefinedValue(this.props.trackStart, this.props.min, 0);
    const max = getFirstDefinedValue(this.props.trackEnd, this.props.max, 0);
    return percentX * (max - min) + min;
  }
  renderLabel(shorthandSize, value, label, name) {
    const use = shorthandSize === 'lg' ? null : 'help';
    const calculatedClassName = classNames('private-slider__label', SLIDER_LABELS[name]);
    const Label = shorthandSize === 'lg' ? H5 : Small;
    return /*#__PURE__*/_jsx(Label, {
      className: calculatedClassName,
      onClick: name === 'min' ? this.handleMinLabelClick : this.handleMaxLabelClick,
      use: use,
      children: label || value
    });
  }
  renderTrackSegment(disabled, readOnly, restrictedAreaTooltip, shorthandSize, _ref) {
    let {
        value,
        restricted,
        width,
        left
      } = _ref,
      trackSegmentProps = _objectWithoutPropertiesLoose(_ref, _excluded);
    if (width <= 0) {
      return null;
    }
    const track = /*#__PURE__*/_jsx(UISliderTrackSegment, Object.assign({}, trackSegmentProps, {
      left: left,
      width: width,
      size: shorthandSize === 'lg' ? shorthandSize : 'md',
      restricted: restricted,
      disabled: disabled,
      readOnly: readOnly,
      value: value
    }));
    if (restrictedAreaTooltip) {
      const tooltipTitle = typeof restrictedAreaTooltip === 'function' ? restrictedAreaTooltip(value > 0 ? 'upper' : 'lower') : restrictedAreaTooltip;
      return /*#__PURE__*/_jsx(UITooltip, {
        title: tooltipTitle,
        placement: "bottom",
        children: track
      });
    }
    return track;
  }
  renderTickMarks(min, value, step, renderedMax, renderedMin) {
    const numberOfTickMarks = (renderedMax - renderedMin) / step;
    const tickMarks = [];
    for (let i = 0; i <= numberOfTickMarks; i++) {
      tickMarks.push( /*#__PURE__*/_jsx(UISliderTickMark, {
        currentSliderValue: value,
        value: i * step + renderedMin,
        min: min
      }, i));
    }
    return tickMarks;
  }
  render() {
    const _this$props = this.props,
      {
        className,
        disabled,
        min,
        minLabel,
        max,
        maxLabel,
        trackStart,
        trackEnd,
        readOnly,
        size,
        showLabels,
        showTickMarks,
        step,
        tabIndex,
        value,
        restrictedAreaTooltip
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    if (process.env.NODE_ENV !== 'production') {
      if (Math.abs(min % step) !== 0 || Math.abs(max % step) !== 0) {
        devLogger.warn({
          message: 'UISlider: Min and max value should be a multiple of the step',
          key: 'UISlider: Min or max not a multiple of the step'
        });
      }
      if (trackStart != null && trackStart > min) {
        devLogger.warn({
          message: 'UISlider: trackStart value should be less than the min',
          key: 'UISlider: trackStart greater than min'
        });
      }
      if (trackEnd != null && trackEnd < max) {
        devLogger.warn({
          message: 'UISlider: trackEnd value should be greater than the max',
          key: 'UISlider: trackEnd less than max'
        });
      }
    }
    const shorthandSize = toShorthandSize(size);
    const computedClassName = classNames(className, 'private-slider', disabled && 'private-slider--disabled', readOnly && 'private-slider--readonly', this.state.isDragging && 'private-slider--dragging', shorthandSize === 'lg' && 'private-slider--large');
    const adjustedValue = getAdjustedValue(min, max, step, value);
    const renderedMin = getFirstDefinedValue(trackStart, min, 0);
    const renderedMax = getFirstDefinedValue(trackEnd, max, 0);
    const trackLength = renderedMax - renderedMin;
    const controlPosition = getControlPositionFromValue(renderedMin, renderedMax, adjustedValue);
    let percentRestrictedLower = 0;
    let percentRestrictedUpper = 0;
    let percentFilled = 0;
    let percentAvailable = 0;
    if (trackLength > 0) {
      percentRestrictedLower = (min - renderedMin) / trackLength * 100;
      percentRestrictedUpper = (renderedMax - max) / trackLength * 100;
      percentFilled = controlPosition - percentRestrictedLower;
      percentAvailable = 100 - percentRestrictedUpper - controlPosition;
    }
    return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
      "aria-disabled": disabled,
      "aria-readonly": readOnly,
      "aria-valuemax": max,
      "aria-valuemin": min,
      "aria-valuenow": value || 0,
      "aria-valuetext": `${value || 0}`,
      className: computedClassName,
      onKeyDown: this.handleKeyDown,
      role: "slider",
      tabIndex: getTabIndex(disabled, tabIndex),
      children: [showLabels && this.renderLabel(shorthandSize, renderedMin, minLabel, 'min'), /*#__PURE__*/_jsx(UIAbstractSlider, {
        className: "private-slider__slider",
        onDrag: this.handleDrag,
        onDragStart: this.handleDragStart,
        onDragEnd: this.handleDragEnd,
        children: /*#__PURE__*/_jsxs("div", {
          className: "private-slider__slider-track",
          children: [/*#__PURE__*/_jsx("div", {
            className: "private-slider__tickmarks",
            children: showTickMarks && this.renderTickMarks(min, value, step, renderedMax, renderedMin)
          }), this.renderTrackSegment(disabled, readOnly, restrictedAreaTooltip, shorthandSize, {
            width: percentRestrictedLower,
            restricted: true,
            value: -1
          }), this.renderTrackSegment(disabled, readOnly, null, shorthandSize, {
            left: percentRestrictedLower,
            width: percentFilled,
            value: 0
          }), this.renderTrackSegment(disabled, readOnly, null, shorthandSize, {
            left: controlPosition,
            width: percentAvailable,
            value: 1
          }), this.renderTrackSegment(disabled, readOnly, restrictedAreaTooltip, shorthandSize, {
            left: controlPosition + percentAvailable,
            width: percentRestrictedUpper,
            restricted: true,
            value: 1
          }), /*#__PURE__*/_jsx("div", {
            className: "private-slider__control",
            style: {
              left: `${controlPosition}%`
            }
          })]
        })
      }), showLabels && this.renderLabel(shorthandSize, renderedMax, maxLabel, 'max')]
    }));
  }
}
UISlider.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onRawValueChange: PropTypes.func,
  min: PropTypes.number,
  minLabel: PropTypes.node,
  max: PropTypes.number.isRequired,
  maxLabel: PropTypes.node,
  trackStart: PropTypes.number,
  trackEnd: PropTypes.number,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseMove: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseUp: PropTypes.func,
  showLabels: PropTypes.bool,
  size: PropTypes.oneOfType([propTypeForSizes(['lg']), PropTypes.oneOf(['default'])]),
  step: PropTypes.number.isRequired,
  showTickMarks: PropTypes.bool,
  value: PropTypes.number,
  readOnly: PropTypes.bool,
  restrictedAreaTooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
};
UISlider.defaultProps = {
  min: 0,
  max: 100,
  step: 1,
  value: null
};
UISlider.displayName = 'UISlider';
export default Controllable(UISlider);